import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import PreviewTemplate from 'components/common/previewTemplate';
import { SecondaryButton } from 'components/forms/atoms/FormButtons';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import type React from 'react';
import { useState } from 'react';
import {
  useDeleteBusinessUnitTemplateMutation,
  useGetBusinessUnitTemplatesQuery,
} from 'services/api/businessUnits.api';

interface BusinessUnitTemplatesProps {
  businessUnitId: number;
}

const BusinessUnitTemplates: React.FC<BusinessUnitTemplatesProps> = ({ businessUnitId }) => {
  const { data: templates, isLoading, error } = useGetBusinessUnitTemplatesQuery(businessUnitId);
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteBusinessUnitTemplateMutation();
  const [openNewTemplate, setOpenNewTemplate] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<{ name: string } | null>(null);
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = useState(false);

  const handleDeleteClick = (templateName: string) => {
    setTemplateToDelete({ name: templateName });
    setIsConfirmDeleteOpen(true);
  };
  if (isLoading) {
    return <Typography>Cargando plantillas...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error al cargar las plantillas</Typography>;
  }

  if (templates === undefined) {
    return null;
  }

  return (
    <Box>
      <SecondaryButton onClick={() => setOpenNewTemplate(true)} startIcon={<AddIcon />} sx={{ mb: 2 }}>
        Crear nueva plantilla
      </SecondaryButton>
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid size={{ xs: 12, md: 4 }} key={template.name}>
            <Box
              sx={{
                mb: 2,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                }}
              >
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleDeleteClick(template.name)}
                  sx={{
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box>
                <PreviewTemplate
                  cardTitle={`${template.name} (${template.category})`}
                  headerEnabled={template.components.some((c) => c.type === 'HEADER')}
                  headerText={template.components.find((c) => c.type === 'HEADER')?.text || null}
                  headerFormat={template.components.find((c) => c.type === 'HEADER')?.format || null}
                  headerFileUrl={
                    template.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                      ?.header_handle?.[0] || null
                  }
                  bodyText={template.components.find((c) => c.type === 'BODY')?.text || ''}
                  bodyVariableExamples={
                    template.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] ||
                    template.components.find((c) => c.type === 'BODY')?.example?.body_text_named_params ||
                    null
                  }
                  footerEnabled={template.components.some((c) => c.type === 'FOOTER')}
                  footerText={template.components.find((c) => c.type === 'FOOTER')?.text || null}
                  buttons={template.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
                  description={
                    <Box
                      sx={{
                        m: 1,
                      }}
                    >
                      <Typography>Variables:</Typography>
                      {(() => {
                        const body = template.components.find((c) => c.type === 'BODY');
                        const namedParams = body?.example?.body_text_named_params;
                        if (namedParams?.length) {
                          return namedParams.map((param) => (
                            <li key={param.param_name}>
                              {param.param_name}: {param.example}
                            </li>
                          ));
                        }
                        return body?.example?.body_text?.[0]?.map((text) => <li key={text}>{text}</li>) ?? null;
                      })()}
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Modal open={openNewTemplate} onClose={() => setOpenNewTemplate(false)}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', md: '70%' },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: (theme) => theme.shadows[24],
            p: 4,
          }}
        >
          <CreateTemplateForm businessUnitId={businessUnitId} />
        </Paper>
      </Modal>
      <Dialog open={isConfirmDeleteOpen} onClose={() => !isDeleting && setIsConfirmDeleteOpen(false)}>
        <DialogTitle>¿Estás seguro que deseas borrar esta plantilla?</DialogTitle>
        <DialogContent>
          <DialogContentText>Esta acción puede romper algún flujo que dependa de esta plantilla.</DialogContentText>
          {isDeleting && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mt: 2,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2">Borrando plantilla...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeleteOpen(false)} disabled={isDeleting}>
            No
          </Button>
          <Button
            color="error"
            disabled={isDeleting}
            onClick={() => {
              if (!templateToDelete) {
                return;
              }
              deleteTemplate({ businessUnitId, name: templateToDelete.name })
                .unwrap()
                .then(() => {
                  setIsConfirmDeleteOpen(false);
                  setShowDeleteSuccessMessage(true);
                  setTemplateToDelete(null);
                  // Cache invalidation will automatically refresh the templates list
                })
                .catch((err: unknown) => {
                  console.error(err);
                  setIsConfirmDeleteOpen(false);
                });
            }}
            startIcon={isDeleting ? <CircularProgress size={16} /> : null}
          >
            {isDeleting ? 'Borrando...' : 'Sí, borrar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={showDeleteSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowDeleteSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ border: '1px solid', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          ¡Template eliminado exitosamente!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessUnitTemplates;
